import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, comics, getComicsByCategory } from '@/mocks/comics';

const sortOptions = [
  { value: 'popular', label: '最热门' },
  { value: 'latest', label: '最新更新' },
  { value: 'rating', label: '评分最高' },
  { value: 'chapters', label: '话数最多' },
];

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [sortBy, setSortBy] = useState('popular');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug, searchQuery]);

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  let filtered = getComicsByCategory(slug || 'all');

  if (searchQuery) {
    filtered = filtered.filter((c) =>
      c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.tags.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }

  switch (sortBy) {
    case 'latest':
      filtered = [...filtered].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
      break;
    case 'rating':
      filtered = [...filtered].sort((a, b) => b.rating - a.rating);
      break;
    case 'chapters':
      filtered = [...filtered].sort((a, b) => b.chapters - a.chapters);
      break;
    default:
      filtered = [...filtered].sort((a, b) => b.views.localeCompare(a.views, undefined, { numeric: true })).reverse();
  }

  const categoryTitle = searchQuery
  ? `搜索：${searchQuery}`
  : currentCategory.name;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: searchQuery ? '搜索' : '分类' },
    ...(searchQuery ? [] : [{ label: currentCategory.name }]),
  ];

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${categoryTitle} - 叮咚漫画网`,
          description: `叮咚漫画网${categoryTitle}分类，提供韩漫免费阅读和韩国漫画在线浏览服务。`,
        })}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 md:pt-24">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="mt-4 mb-6">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                {categoryTitle}
              </h1>
              {searchQuery && (
                <p className="mt-2 text-sm text-foreground-500">
                  找到 {filtered.length} 个相关漫画
                </p>
              )}
            </div>

            {/* Category Pills */}
            <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-6 border-b border-background-200">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-full transition-colors whitespace-nowrap flex-shrink-0 ${
                    slug === cat.slug && !searchQuery
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </a>
              ))}
            </div>

            {/* Sort Bar */}
            <div className="flex items-center justify-between mb-6">
              <span className="text-sm text-foreground-500">
                共 <strong className="text-foreground-900">{filtered.length}</strong> 部漫画
              </span>
              <div className="flex items-center gap-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1.5 text-xs rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-foreground-900 text-background-50'
                        : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Comic Grid */}
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5 pb-12">
                {filtered.map((comic) => (
                  <ComicCard key={comic.id} {...comic} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-background-100 rounded-full mb-4">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <h3 className="text-lg font-medium text-foreground-700 mb-2">暂无相关漫画</h3>
                <p className="text-sm text-foreground-500 mb-4">没有找到与"{searchQuery}"相关的漫画</p>
                <a
                  href="/category/all"
                  className="px-4 py-2 text-sm bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  浏览全部漫画
                </a>
              </div>
            )}
          </div>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}